import { type ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronRight } from "lucide-react";
import Submenu from "./Submenu";
import { useMenuContext } from "../../hooks/context/useMenuContext";
import { useContextMenuConfig } from "../../hooks/grid/useContextMenuConfig";
import { cn } from "../../../../../lib/utils";
import "./style.scss";

interface ContextMenuProps {
    children?: ReactNode;
    openActionsModal?: any;
    openCopyModal?: any;
    openDeleteModal?: any;
    openAssignModal?: any;
    openProfileSheet?: any;
    openTransferModal?: any;
    openShiftTradeDrawer?: any;
    openClaimShiftDrawer?: any;
    openSaveDayAsTemplate?: any;
    openLoadTemplatesModal?: any;
    openMoveToOpenShiftsModal?: any;
}

function Menu({
    children,
    openActionsModal,
    openCopyModal,
    openDeleteModal,
    openAssignModal,
    openProfileSheet,
    openTransferModal,
    openShiftTradeDrawer,
    openClaimShiftDrawer,
    openSaveDayAsTemplate,
    openLoadTemplatesModal,
    openMoveToOpenShiftsModal,
}: ContextMenuProps) {
    const menuRef = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState(false);

    // Position state
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [submenuDirection, setSubmenuDirection] = useState<"left" | "right">("right");
    const [openSubmenu, setOpenSubmenu] = useState<String | null>(null);

    const config = useContextMenuConfig({
        openActionsModal,
        openCopyModal,
        openDeleteModal,
        openAssignModal,
        openProfileSheet,
        openTransferModal,
        openShiftTradeDrawer,
        openClaimShiftDrawer,
        openSaveDayAsTemplate,
        openLoadTemplatesModal,
        openMoveToOpenShiftsModal,
    });

    const { id, x, y, closeMenu } = useMenuContext();

    // 1. Initialize visibility
    useEffect(() => {
        if (!!id) {
            setIsVisible(true);
            // We set a temporary position; useLayoutEffect will correct it immediately
            setPosition({ x: x || 0, y: y || 0 });
        } else {
            setIsVisible(false);
            setOpenSubmenu(null);
        }
    }, [id, x, y]);

    // 2. Calculate Position & Flip Logic (Run before paint)
    useLayoutEffect(() => {
        if (!isVisible || !menuRef.current) return;

        // A. Measure Menu
        const { offsetWidth: menuWidth, offsetHeight: menuHeight } = menuRef.current;

        // B. Measure Viewport (Use documentElement to EXCLUDE scrollbars)
        const viewportWidth = document.documentElement.clientWidth;
        const viewportHeight = document.documentElement.clientHeight;
        const scrollX = window.scrollX;
        const scrollY = window.scrollY;

        // C. Normalize Coordinates
        // If x is larger than the viewport + some buffer, it's likely a Page Coordinate (pageX).
        // Since we use position: fixed, we need Viewport Coordinates (clientX).
        let clientX = x || 0;
        let clientY = y || 0;

        if (clientX > viewportWidth && scrollX > 0) {
            clientX = clientX - scrollX;
        }
        if (clientY > viewportHeight && scrollY > 0) {
            clientY = clientY - scrollY;
        }

        let newX = clientX;
        let newY = clientY;

        // D. Flip Logic (X Axis)
        // If menu extends past the right edge, flip to left
        if (clientX + menuWidth > viewportWidth) {
            newX = clientX - menuWidth;
        }
        // Safety: Don't let it go off the left edge
        if (newX < 0) {
            newX = 0;
        }

        // E. Flip Logic (Y Axis)
        // If menu extends past the bottom edge, flip up
        if (clientY + menuHeight > viewportHeight) {
            newY = clientY - menuHeight;
        }
        // Safety: Don't let it go off the top edge
        if (newY < 0) {
            newY = 0;
        }

        setPosition({ x: newX, y: newY });

        // F. Submenu Direction
        // If menu is on the right half of screen, open submenus to the left
        if (clientX > viewportWidth / 2) {
            setSubmenuDirection("left");
        } else {
            setSubmenuDirection("right");
        }

    }, [isVisible, x, y]);

    // 3. Close handlers
    useEffect(() => {
        if (!isVisible) return;

        const handleOutsideClick = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setIsVisible(false);
                setOpenSubmenu(null);
                closeMenu();
            }
        };

        const handleScroll = (e: Event) => {
            const target = e.target as HTMLElement;

            // 1. Ignore if the scroll target is inside the context menu DOM
            if (menuRef.current?.contains(target)) {
                return;
            }

            // 2. Ignore if the scroll target is part of a Radix portal (like the ResourceSelect popover)
            // Radix UI (used by Shadcn) adds data attributes to its portal containers.
            if (target.closest('[data-radix-portal]') || target.closest('.overflow-auto')) {
                // If the element being scrolled is a portaled overlay or a scrollable container
                // we check if it's "part" of our logical menu flow. 
                // Usually, checking if the target is NOT the document or main window is enough.
                if (target !== (document as any) && target !== document.documentElement && target !== (window as any)) {
                    return;
                }
            }

            setIsVisible(false);
            setOpenSubmenu(null);
            closeMenu();
        };

        document.addEventListener("mousedown", handleOutsideClick);
        // Keep capture: true to catch scrolls before they are swallowed, 
        // but the logic above will now filter them.
        document.addEventListener("scroll", handleScroll, { capture: true });

        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
            document.removeEventListener("scroll", handleScroll, { capture: true });
        };
    }, [isVisible, closeMenu]);

    const content = useMemo(() => {
        if (!id) return null;

        const menuConfig = (config as any)[id];
        if (!menuConfig) return null;

        return menuConfig.map((item: any, i: number) => {
            const Icon = item.icon;
            const hasSubmenu = !!item?.submenu;
            const isOpen = openSubmenu === item.submenu?.key;

            return (
                <div
                    key={`${id}-${i}`}
                    className="tw-relative tw-w-full"
                    onMouseLeave={() => {
                        // if (hasSubmenu && openSubmenu === item.submenu.key) {
                        //     setOpenSubmenu(null);
                        // }
                    }}
                >
                    <button
                        className={cn(
                            "context-menu-item tw-flex tw-items-center tw-gap-2 tw-justify-between tw-w-full tw-text-left tw-px-2 tw-py-1.5 hover:tw-bg-neutral-100 tw-rounded-sm tw-text-sm tw-transition-colors",
                            item.hidden && "tw-hidden",
                            item.disabled && "tw-opacity-50 tw-cursor-not-allowed"
                        )}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (hasSubmenu) {
                                setOpenSubmenu(item.submenu.key);
                            } else {
                                item.onChange();
                                setIsVisible(false);
                                closeMenu();
                            }
                        }}
                        onMouseEnter={() => {
                            if (hasSubmenu) {
                                setOpenSubmenu(item.submenu.key);
                            } else {
                                setOpenSubmenu(null);
                            }
                        }}
                        disabled={item.disabled}
                    >
                        <span className="tw-flex tw-items-center tw-gap-2">
                            {Icon && <Icon className="tw-w-4 tw-h-4" />} {item.label}
                        </span>

                        {hasSubmenu && <ChevronRight size={14} />}
                    </button>

                    {(hasSubmenu && isOpen) && (
                        <Submenu
                            direction={submenuDirection}
                            className="tw-z-10"
                            onMouseDown={(e) => e.stopPropagation()}
                            onMouseLeave={() => {
                                setOpenSubmenu(null);
                            }}
                            onMouseEnter={() => {
                                setOpenSubmenu(item.submenu.key);
                            }}
                        >
                            {item.submenu.content}
                        </Submenu>
                    )}
                </div>
            );
        });
    }, [id, config, openSubmenu, submenuDirection, closeMenu]);

    if (!isVisible) {
        return null;
    }

    if ((config as any)?.[id]?.length === 0) {
        return null;
    }

    if (children === "no-content") {
        return null;
    }

    return createPortal(
        <div
            key={id}
            ref={menuRef}
            className="custom-context-menu tw-fixed tw-bg-white tw-border tw-rounded-md tw-shadow-lg tw-p-1 tw-min-w-[180px] tw-z-50 tw-animate-in tw-fade-in-0 tw-zoom-in-95"
            style={{
                left: position.x,
                top: position.y
            }}
            onMouseDown={(e) => e.stopPropagation()}
        >
            {children ? children : content}
        </div>,
        document.body,
    );
}

export default Menu;